import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Button } from 'react-bootstrap';
import dayjs from '../util/date.js';
import {
	COLOR_BY,
	RANGE,
	activitiesForDay,
	buildMonthGrid,
	buildWeekGrid,
	legendItems,
	summarizeProjectFilter
} from '../util/CalendarLogic.jsx';
import { isWorkDay, projectCalendar } from '../util/workCalendar.js';
import CalendarChip from './CalendarChip.jsx';
import CalendarOverflow from './CalendarOverflow.jsx';

const MAX_CHIPS_MONTH = 4;
const MAX_CHIPS_WEEK = 18;
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const dayHeading = (day, range, index) => {
	if (range !== RANGE.WEEK) {
		return String(day.date);
	}
	const d = dayjs(day.ms);
	if (index === 0 || day.date === 1) {
		return d.format('ddd D MMM');
	}
	return d.format('ddd D');
};

const CalendarView = ({
	filteredActivities,
	datasetInfo,
	projectFilter
}) => {
	const [anchorMs, setAnchorMs] = useState(() => Date.now());
	const [range, setRange] = useState(RANGE.MONTH);
	const [colorBy, setColorBy] = useState(COLOR_BY.STATUS);
	const [codeTypeId, setCodeTypeId] = useState(() => {
		const types = datasetInfo && datasetInfo.activityCodeTypes ? datasetInfo.activityCodeTypes : {};
		const seq = datasetInfo && datasetInfo.activityCodeTypesSequence;
		if (Array.isArray(seq) && seq.length) {
			return String(seq[0]);
		}
		const keys = Object.keys(types);
		return keys.length ? String(keys[0]) : '';
	});

	const isWeek = range === RANGE.WEEK;
	const grid = useMemo(
		() => (isWeek ? buildWeekGrid(anchorMs) : buildMonthGrid(anchorMs)),
		[anchorMs, isWeek]
	);
	const activities = Array.isArray(filteredActivities) ? filteredActivities : [];
	const codeTypes = datasetInfo && datasetInfo.activityCodeTypes ? datasetInfo.activityCodeTypes : {};
	const legend = useMemo(
		() => legendItems(colorBy, codeTypeId, datasetInfo, activities),
		[colorBy, codeTypeId, datasetInfo, activities]
	);
	const shadeCal = projectCalendar(datasetInfo);
	const filterSummary = summarizeProjectFilter(projectFilter);
	const printedAt = dayjs().format('DD-MMM-YYYY HH:mm');
	const maxChips = isWeek ? MAX_CHIPS_WEEK : MAX_CHIPS_MONTH;

	const goPrev = () => {
		setAnchorMs(dayjs(grid.monthStartMs).subtract(1, isWeek ? 'week' : 'month').valueOf());
	};
	const goNext = () => {
		setAnchorMs(dayjs(grid.monthStartMs).add(1, isWeek ? 'week' : 'month').valueOf());
	};
	const goToday = () => {
		setAnchorMs(Date.now());
	};

	return (
		<div className={classnames('calendarView', { 'calendarView--week': isWeek, 'calendarView--month': !isWeek })}>
			<div className="calendarToolbar no-print">
				<div className="calendarNav">
					<Button onClick={goPrev} className="calendarNavBtn">‹</Button>
					<h2 className="calendarMonthTitle">{grid.monthLabel}</h2>
					<Button onClick={goNext} className="calendarNavBtn">›</Button>
					<Button onClick={goToday} className="calendarTodayBtn">Today</Button>
					<div className="calendarRange">
						<label>
							<input
								type="radio"
								name="calendarRange"
								checked={!isWeek}
								onChange={() => { setRange(RANGE.MONTH); }} />
							Month
						</label>
						<label>
							<input
								type="radio"
								name="calendarRange"
								checked={isWeek}
								onChange={() => { setRange(RANGE.WEEK); }} />
							Week
						</label>
					</div>
				</div>
				<div className="calendarColorBy">
					<label>
						Color by
						<select
							value={colorBy}
							onChange={e => { setColorBy(e.target.value); }}>
							<option value={COLOR_BY.STATUS}>Status</option>
							<option value={COLOR_BY.CODE}>Activity code</option>
							<option value={COLOR_BY.RESOURCE}>Resource</option>
						</select>
					</label>
					{colorBy === COLOR_BY.CODE
						? (
							<select
								value={codeTypeId}
								onChange={e => { setCodeTypeId(e.target.value); }}>
								{Object.keys(codeTypes).map(id => {
									const type = codeTypes[id];
									const name = type && typeof type === 'object' ? type.name : type;
									return (
										<option key={id} value={id}>{name || id}</option>
									);
								})}
							</select>
						)
						: null}
					<Button
						className="calendarPrintBtn"
						onClick={() => { window.print(); }}>
						Print
					</Button>
				</div>
			</div>

			<div className="calendarPrintHeader">
				<div><strong>{datasetInfo && datasetInfo.Name ? datasetInfo.Name : 'Project'}</strong></div>
				<div>{isWeek ? 'Week' : 'Month'}: {grid.monthLabel}</div>
				<div>Printed: {printedAt}</div>
				<div>Filters: {filterSummary}</div>
			</div>

			{isWeek
				? null
				: (
					<div className="calendarWeekdays">
						{WEEKDAY_LABELS.map(label => (
							<div key={label} className="calendarWeekday">{label}</div>
						))}
					</div>
				)}

			<div className={classnames('calendarGrid', { 'calendarGrid--week': isWeek })}>
				{grid.days.map((day, index) => {
					const workDay = isWorkDay(day.key, shadeCal);
					const dayActivities = activitiesForDay(activities, day.ms, {
						codeTypeId,
						datasetInfo
					});
					const visible = dayActivities.slice(0, maxChips);
					const overflow = dayActivities.length - visible.length;
					return (
						<div
							key={day.key}
							className={classnames('calendarDay', {
								'calendarDay--outside': !day.inMonth,
								'calendarDay--today': day.isToday,
								'calendarDay--nonWorking': !workDay
							})}>
							<div className="calendarDayNum">{dayHeading(day, range, index)}</div>
							<div className="calendarDayChips">
								{visible.map(activity => (
									<CalendarChip
										key={activity.activityId}
										activity={activity}
										dayKey={day.key}
										datasetId={datasetInfo && datasetInfo.Id}
										colorBy={colorBy}
										codeTypeId={codeTypeId}
										datasetInfo={datasetInfo} />
								))}
								{overflow > 0
									? (
										<CalendarOverflow
											dayKey={day.key}
											activities={dayActivities.slice(maxChips)}
											datasetId={datasetInfo && datasetInfo.Id}
											colorBy={colorBy}
											codeTypeId={codeTypeId}
											datasetInfo={datasetInfo} />
									)
									: null}
							</div>
						</div>
					);
				})}
			</div>

			<div className="calendarLegend">
				{legend.map(item => (
					<div key={item.key} className="calendarLegendItem">
						<span className="calendarLegendSwatch" style={{ backgroundColor: item.color }} />
						<span>{item.label}</span>
					</div>
				))}
			</div>
		</div>
	);
};

CalendarView.propTypes = {
	filteredActivities: PropTypes.array,
	datasetInfo: PropTypes.object,
	projectFilter: PropTypes.object
};

export default CalendarView;
